<template>
    <div v-if="model">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-select
                            :options="form.competencias"
                            :validations="validations.competencia"
                            @change="handleCompetenciaChange"
                            label="Competência"
                            show-default-option
                            text-field="valor"
                            v-model="model.competenciaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-select
                            :options="form.tiposInconsistencia"
                            :validations="validations.tipoInconsistencia"
                            @change="handleTipoInconsistenciaChange"
                            label="Tipo de inconsistência"
                            show-default-option
                            text-field="descricao"
                            v-model="model.tipoInconsistencia"
                            value-field="valor"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :options="form.unidades"
                            @change="handleUnidadeChange"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <template v-if="data">
            <rkm-area
                no-margin-top
                title="Resultado"
            >
                <template v-slot:trailing>
                    <rkm-button
                        @click="print"
                        color="info"
                        small
                        title="Imprimir"
                    >
                        <rkm-icon icon="print" />
                    </rkm-button>

                    <rkm-button
                        @click="exportTo"
                        color="info"
                        small
                        title="Exportar"
                    >
                        <rkm-icon icon="file-export" />
                    </rkm-button>
                </template>

                <rkm-table
                    :fields="fields"
                    :items="data.itens"
                    :sort-by="sortBy"
                    :sort-desc="sortDesc"
                    @sort-changed="onSortChanged"
                >
                    <template v-slot:cell(inconsistencias)="{item}">
                        <div
                            v-for="(inconsistencia, index) in item.inconsistencias"
                            :key="uniqueId(`inconsistencia-${index}`)"
                        >
                            {{inconsistencia.inconsistenciaDescricao}}
                        </div>
                    </template>
                </rkm-table>
            </rkm-area>
        </template>
    </div>
</template>

<script>
// Mixins.
import {error, file, formats, repository, stringCase} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        validations() {
            return {
                competencia: {required: true},
                tipoInconsistencia: {required: true},
            }
        },
        fields() {
            const fields = []

            if (this.model.tipoInconsistencia === 'atendimento') {
                fields.push({key: 'dataAdmissao', label: 'Data de admissão', sortable: true})
                fields.push({key: 'dataConclusao', label: 'Data de conclusão', sortable: true})
                fields.push({key: 'unidadeNomeFantasia', label: 'Unidade', sortable: true})
                fields.push({key: 'pacienteNome', label: 'Nome do paciente', sortable: true})
                fields.push({key: 'pacienteCns', label: 'CNS do paciente', sortable: true})
                fields.push({key: 'cid', label: 'CID', sortable: true})
                fields.push({key: 'inconsistencias', label: 'Inconsistências'})
            }
            else if (this.model.tipoInconsistencia === 'procedimento') {
                fields.push({key: 'dataAdmissao', label: 'Data de admissão', sortable: true})
                fields.push({key: 'dataConclusao', label: 'Data de conclusão', sortable: true})
                fields.push({key: 'unidadeNomeFantasia', label: 'Unidade', sortable: true})
                fields.push({key: 'pacienteNome', label: 'Nome do paciente', sortable: true})
                fields.push({key: 'pacienteCns', label: 'CNS do paciente', sortable: true})
                fields.push({key: 'registroNome', label: 'Tipo de RAAS', sortable: true})
                fields.push({key: 'procedimento', label: 'Procedimento', sortable: true})
                fields.push({key: 'cid', label: 'CID', sortable: true})
                fields.push({key: 'inconsistencias', label: 'Inconsistências'})
            }

            return fields
        },
    },
    created() {
        this.repositories = {
            exportacao: this.getConectaSusRepository('exportacao.raas'),
            impressao: this.getConectaSusRepository('impressao.raas'),
            relatorioInconsistencia: this.getConectaSusRepository('raas.relatorioInconsistencia'),
        }

        this.load()
    },
    data: () => ({
        data: null,
        form: null,
        model: null,
        others: {moment},
        sortBy: 'dataAdmissao',
        sortDesc: false,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioInconsistencia.create()
                .then((response) => {
                    this.form = response.data.form
                    this.model = response.data.model
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {
            const request = {
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: this.model.tipoInconsistencia,
                unidadeId: this.model.unidadeId,
            }

            this.setLoading()

            this.repositories.relatorioInconsistencia.index(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.data = Object.assign(response.data))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {
            this.form.tiposInconsistencia = []
            this.form.unidades = []

            this.model.competenciaId = null
            this.model.tipoInconsistencia = null
            this.model.unidadeId = null

            this.data = null

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {
            this.setLoading()

            const request = {
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: this.model.tipoInconsistencia,
                unidadeId: this.model.unidadeId,
                sortBy: this.sortBy ? this.camelToSnake(this.sortBy) : null,
                sortDesc: this.sortDesc,
            }

            this.repositories.impressao.relatorioInconsistencia(this.camelObjectKeysToSnakeRecursive(request))
                .then(({data, headers}) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {
            this.setLoading()

            const request = {
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: this.model.tipoInconsistencia,
                unidadeId: this.model.unidadeId,
                sortBy: this.sortBy ? this.camelToSnake(this.sortBy) : null,
                sortDesc: this.sortDesc,
            }

            this.repositories.exportacao.relatorioInconsistencia(this.camelObjectKeysToSnakeRecursive(request))
                .then(({data, headers}) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        handleCompetenciaChange(value) {
            this.form.tiposInconsistencia = []
            this.form.unidades = []
            this.model.tipoInconsistencia = null
            this.model.unidadeId = null

            this.data = null

            if (value === null) {
                return
            }

            const request = {
                listar: 'tipos-inconsistencia',
                competenciaId: value,
            }

            this.setLoading()

            this.repositories.relatorioInconsistencia.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.form.tiposInconsistencia = Object.assign(response.data.tiposInconsistencia))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleTipoInconsistenciaChange(value) {
            this.form.unidades = []
            this.model.unidadeId = null

            this.data = null

            if (value === null) {
                return
            }

            const request = {
                listar: 'unidades',
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: value,
            }

            this.setLoading()

            this.repositories.relatorioInconsistencia.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.form.unidades = Object.assign(response.data.unidades))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUnidadeChange() {
            this.data = null
        },
        onSortChanged(context) {
            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
    },
    mixins: [error, file, formats, repository, stringCase],
}
</script>